export const todayStr = () => new Date().toISOString().slice(0, 10);

export const PHASE_NAMES = { A: "Metal Line", B: "Bottom Forming", C: "Welding & QC", D: "Electrolysis", E: "Buffing", F: "Assembly & Packing" };

const titleCase = (s) => String(s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
export const phaseLabel = (ph) => PHASE_NAMES[ph] || titleCase(ph);

const TONES = {
  A: { chip: "bg-blue-100 text-blue-700", bar: "bg-blue-500", hover: "hover:border-blue-500 hover:ring-blue-100" },
  B: { chip: "bg-amber-100 text-amber-700", bar: "bg-amber-500", hover: "hover:border-amber-500 hover:ring-amber-100" },
  C: { chip: "bg-teal-100 text-teal-700", bar: "bg-teal-500", hover: "hover:border-teal-500 hover:ring-teal-100" },
  D: { chip: "bg-sky-100 text-sky-700", bar: "bg-sky-500", hover: "hover:border-sky-500 hover:ring-sky-100" },
  E: { chip: "bg-orange-100 text-orange-700", bar: "bg-orange-500", hover: "hover:border-orange-500 hover:ring-orange-100" },
};
const DEFAULT_TONE = { chip: "bg-emerald-100 text-emerald-700", bar: "bg-emerald-500", hover: "hover:border-emerald-500 hover:ring-emerald-100" };
export const phaseTone = (ph) => TONES[ph] || DEFAULT_TONE;

export const isRepolishPhase = (ph) => {
  const l = phaseLabel(ph || "").toLowerCase();
  return l.includes("buff") || l.includes("pack") || l.includes("assembly");
};

export function rejPillClass(pct) {
  if (pct > 5) return "bg-red-100 text-red-700";
  if (pct > 2) return "bg-amber-100 text-amber-700";
  return "bg-green-100 text-green-700";
}

export const activeProcs = (processes) => [...processes].filter((p) => p.active !== false).sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

export function groupByPhase(processes) {
  const groups = [];
  activeProcs(processes).forEach((p) => {
    const ph = p.phase || "—";
    let g = groups.find((x) => x.phase === ph);
    if (!g) { g = { phase: ph, procs: [] }; groups.push(g); }
    g.procs.push(p);
  });
  return groups;
}

// Products that can be produced at this process (backend: no link => every process)
export function candidateModels(pid, models, links) {
  return models.filter((m) => { const r = links[m.name]; return !Array.isArray(r) || r.length === 0 || r.includes(pid); });
}

// Ordered route for a model (linked processes, else all)
export function routeFor(model, links, processes) {
  const all = activeProcs(processes);
  const r = links[model];
  return Array.isArray(r) && r.length ? all.filter((p) => r.includes(p.id)) : all;
}

export function nextProcessFor(model, pid, links, processes) {
  const route = routeFor(model, links, processes);
  const idx = route.findIndex((p) => p.id === pid);
  return idx >= 0 && idx < route.length - 1 ? route[idx + 1] : null;
}

export function flattenEntries(dprDocs, processes) {
  const order = {}; processes.forEach((p) => { order[p.id] = p.order ?? 999; });
  const out = [];
  dprDocs.forEach((d) => (d.stages || []).forEach((s) => out.push({
    ...s, dpr_id: d.id, date: d.date, shift: d.shift || "Day", model: d.model,
    phase: processes.find((p) => p.id === s.process_id)?.phase || "",
  })));
  return out.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : (order[a.process_id] ?? 999) - (order[b.process_id] ?? 999)));
}

const fmt = (n) => (Number(n) || 0).toLocaleString();

// Mirrors backend availability rules: coil-fed => selected coil kg / per; item-fed => item stock / per; else WIP
export function availInfo({ consumedItem, per, coil, coilRequired, av }) {
  if (consumedItem) {
    if (consumedItem.is_coil) {
      if (coilRequired && !coil) return { text: "Select a coil", sub: "pick which coil is loaded on the machine", max: 0, needCoil: true };
      const kg = coil?.remaining_weight_kg || 0;
      const max = per > 0 ? Math.floor(kg / per) : Infinity;
      return { text: per > 0 ? `${fmt(max)} pcs` : `${fmt(kg)} kg`, sub: per > 0 ? `${fmt(kg)} kg left on coil · ${per} kg per piece` : `${fmt(kg)} kg left on coil`, max };
    }
    const stock = consumedItem.current_stock || 0;
    const max = per > 0 ? Math.floor(stock / per) : Infinity;
    return { text: per > 0 ? `${fmt(max)} pcs` : `${fmt(stock)} ${consumedItem.base_unit || ""}`, sub: `${consumedItem.name}: ${fmt(stock)} ${consumedItem.base_unit || ""} in stock`, max };
  }
  const a = Math.floor(av?.available ?? 0);
  return { text: `${fmt(a)} pcs`, sub: "ready from the previous stage", max: a };
}
